import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { Observable, of } from 'rxjs'
import { IWeatherStatusModel } from '../models/weather-status.model'
import { map } from 'rxjs/operators'
import { CookieService } from 'ngx-cookie-service'

export type WindDirection =
  | 'N'
  | 'N/NE'
  | 'NE'
  | 'E/NE'
  | 'E'
  | 'E/SE'
  | 'SE'
  | 'S/SE'
  | 'S'
  | 'S/SW'
  | 'SW'
  | 'W/SW'
  | 'W'
  | 'W/NW'
  | 'NW'
  | 'N/NW'

@Injectable({
  providedIn: 'root',
})
export class WeatherStatusService {
  private _apiURL: string = environment.apiUrl + '/weather'
  private cookieName: string = '__spas_weather'

  constructor(private http: HttpClient, private cookieService: CookieService) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json')
  }

  public getLast(
    useCookie: boolean,
  ): Observable<{ weather: IWeatherStatusModel | null; next_at: number }> {
    if (useCookie) {
      const lastFromCookie = this.getLastFromCooke()
      if (lastFromCookie) {
        return of(lastFromCookie)
      }
    }
    return this.http.get(this._apiURL + '/last', { headers: this.getHeaders() }).pipe(
      map((response: { weather: IWeatherStatusModel | null; next_at: number }) => {
        if (useCookie && response.weather && response.next_at) {
          this.saveLast(response.weather, response.next_at)
        }
        return response
      }),
    )
  }

  // Cookie
  private saveLast(weather: IWeatherStatusModel, next_at: number): void {
    this.cookieService.set(
      this.cookieName,
      JSON.stringify({ weather: weather, next_at: next_at, saved_at: new Date().getTime() }),
      this.getExpirationDate(next_at),
    )
  }

  private getExpirationDate(expiresIn: number): Date {
    const date = new Date()
    date.setSeconds(date.getSeconds() + expiresIn)
    return date
  }

  public getLastFromCooke(): { weather: IWeatherStatusModel; next_at: number } | null {
    const jsonData = this.cookieService.get(this.cookieName)
    if (jsonData) {
      const data = JSON.parse(jsonData)
      if (data && data.weather && data.next_at) {
        return {
          weather: data.weather,
          next_at: this.getNextAtBasedOnCookie(data.saved_at, data.next_at),
        }
      }
    }
    return null
  }

  public getNextAtBasedOnCookie(savedAt: number, nextAt: number): number {
    const savedAtDate = new Date(savedAt)
    savedAtDate.setSeconds(savedAtDate.getSeconds() + nextAt)
    const currentTime = new Date().getTime()
    return Math.round((savedAtDate.getTime() - currentTime) / 1000)
  }

  // Wind Direction
  public getWindDirectionBySection(section: string): WindDirection {
    return section in this.getWindDirectionSections()
      ? this.getWindDirectionSections()[section]
      : 'N'
  }

  public getWindSectionByDegree(degree: number): string {
    if (degree > 360) {
      return 's0'
    }
    const section = Math.round(degree / 22.5)
    return section >= 16 ? `s0` : `s${section}`
  }

  public getWindDirectionSections(): any {
    return {
      s0: 'N',
      s1: 'N/NE',
      s2: 'NE',
      s3: 'E/NE',
      s4: 'E',
      s5: 'E/SE',
      s6: 'SE',
      s7: 'S/SE',
      s8: 'S',
      s9: 'S/SW',
      s10: 'SW',
      s11: 'W/SW',
      s12: 'W',
      s13: 'W/NW',
      s14: 'NW',
      s15: 'N/NW',
    }
  }

  public getWindDirectionByDegree(degree: number): WindDirection {
    return this.getWindDirectionBySection(this.getWindSectionByDegree(degree))
  }
}
